import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import {
  Search,
  ArrowUpDown,
  TrendingUp,
  TrendingDown,
  Star,
  ChevronLeft,
  ChevronRight,
  Download,
  SlidersHorizontal,
  X,
  Loader2,
  Globe,
  Calendar,
  Droplets,
  BarChart3,
  ShieldAlert,
  Lock,
  AlertCircle,
  ArrowRight
} from 'lucide-react';
import { stocksAPI, watchlistsAPI, analysisAPI } from '../services/api';
import { getToken, removeToken } from '../services/api';
import { markets, formatCurrency, formatMarketCap } from '../data/mockData';
import { MetricLabel } from '../components/MetricTooltip';
import MacroWidget from '../components/MacroWidget';
import { ValuationBadgeCompact, ValuationScore } from '../components/ValuationBadge';
import { LiquidityBadgeCompact } from '../components/LiquidityScore';
import { RedFlagBadgeCompact, analyzeQualityFlags } from '../components/Analysis/RedFlagBadge';
import { ValoramapScoreBadge } from '../components/ValoramapScore';
import ScoringBadge from '../components/ScoringBadge';
import ValoramapScoreBasisBadge from '../components/ValoramapScoreBasisBadge';
import axios from 'axios';

// Helper to get liquidity label from score
const getLiquidityLabel = (score) => {
  if (score >= 70) return 'Élevée';
  if (score >= 40) return 'Modérée';
  return 'Faible';
};

// Helper to get VALORAMAP score label
const getScoreLabel = (score) => {
  if (score >= 80) return 'Opportunité exceptionnelle';
  if (score >= 60) return 'Opportunité forte';
  if (score >= 40) return 'Opportunité modérée';
  if (score >= 20) return 'À surveiller';
  return 'Risque élevé';
};

// Sector name translations (EN → FR)
// Values sent to API stay in English; dropdown displays French labels
const SECTOR_TRANSLATIONS = {
  "Financial Services": "Services Financiers",
  "Financials": "Finance",
  "Banks": "Banques",
  "Banking": "Banques",
  "Technology": "Technologie",
  "Information Technology": "Technologies de l'Information",
  "Software": "Logiciels",
  "Healthcare": "Santé",
  "Health Care": "Santé",
  "Real Estate": "Immobilier",
  "Energy": "Énergie",
  "Industrials": "Industrie",
  "Industrial": "Industrie",
  "Materials": "Matériaux",
  "Basic Materials": "Matières Premières",
  "Consumer Discretionary": "Consommation Discrétionnaire",
  "Consumer Staples": "Biens de Première Nécessité",
  "Consumer Cyclical": "Consommation Cyclique",
  "Consumer Defensive": "Consommation Défensive",
  "Consumer Non-Cyclicals": "Consommation Non-Cyclique",
  "Consumer Goods": "Biens de Consommation",
  "Utilities": "Services Publics",
  "Communication Services": "Services de Communication",
  "Telecommunications": "Télécommunications",
  "Automotive": "Automobile",
};

// Fallback inflation rates (used only if API fails)
const FALLBACK_INFLATION = {
  VN: 3.1, NSE: 4.2, IN: 4.2, PH: 2.5, PSE: 2.5,
  ID: 1.4, JK: 1.4, PL: 4.3, WSE: 4.3, KR: 2.0, KRX: 2.0
};
const US_INFLATION = 2.0;
const INFLATION_CACHE_KEY = 'inflation_rates';
const INFLATION_CACHE_TIME_KEY = 'inflation_cache_time';
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

// Public API for preview mode (no auth)
const PUBLIC_API_URL = 'https://api-ovh.alainponcelas.com/api';

// Access modes
const ACCESS_MODES = {
  FULL: 'full',           // Logged in with active subscription
  NO_SUBSCRIPTION: 'no_subscription', // Logged in but no subscription
  GUEST: 'guest'          // Not logged in
};

const ScreenerPage = ({ user }) => {
  // Read URL parameters for pre-filtering
  const [searchParams] = useSearchParams();
  const countryParam = searchParams.get('country');
  const catalystFtseParam = searchParams.get('catalyst_ftse_eligible');
  const sortParam = searchParams.get('sort');
  const orderParam = searchParams.get('order');
  
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalStocks, setTotalStocks] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [inflationRates, setInflationRates] = useState(FALLBACK_INFLATION);
  const [qualityCache, setQualityCache] = useState({}); // Cache for accounting quality data
  
  // Access control state
  const [accessMode, setAccessMode] = useState(null); // null = checking, then FULL/NO_SUBSCRIPTION/GUEST
  const [totalAvailable, setTotalAvailable] = useState(6513); // Total stocks for display in preview banner

  const [searchQuery, setSearchQuery] = useState('');
  // Initialize selectedMarket from URL param if present
  const [selectedMarket, setSelectedMarket] = useState(countryParam || 'all');
  const [selectedSector, setSelectedSector] = useState('all');
  const [sectors, setSectors] = useState([]);
  const [peRange, setPeRange] = useState({ min: '', max: '' });
  const [pbRange, setPbRange] = useState({ min: '', max: '' });
  const [roeRange, setRoeRange] = useState({ min: '', max: '' });
  const [realPeMax, setRealPeMax] = useState('');
  const [realRoeMin, setRealRoeMin] = useState('');
  const [qualityFilter, setQualityFilter] = useState('all'); // Filter for quality flags
  const [scoreMin, setScoreMin] = useState('');
  const [liquidityMin, setLiquidityMin] = useState('');
  const [hasAdrFilter, setHasAdrFilter] = useState('all');
  // Initialize catalyst filter from URL param
  const [catalystFtseEligible, setCatalystFtseEligible] = useState(catalystFtseParam === 'true');
  // Scoring filters (visible above table, not hidden in advanced filters)
  const [hideFinancials, setHideFinancials] = useState(true); // Activé par défaut
  const [hideInsufficientData, setHideInsufficientData] = useState(false); // Désactivé par défaut
  // Initialize sort from URL params
  const [sortConfig, setSortConfig] = useState({ 
    key: sortParam || 'marketCap', 
    direction: orderParam || 'desc' 
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [showRealMetrics, setShowRealMetrics] = useState(false); // Hide Real P/E and Real ROE by default
  const [watchlist, setWatchlist] = useState([]);
  const itemsPerPage = 15;
  
  // TODO post-launch: move filtering to backend query params for consistent pagination
  // Currently fetching more results and filtering client-side
  const apiLimit = 50; // Fetch more to compensate for client-side filtering
  
  // Preview mode uses only 10 items
  const previewLimit = 10;
  const isPreviewMode = accessMode === ACCESS_MODES.GUEST || accessMode === ACCESS_MODES.NO_SUBSCRIPTION;

  // Check access mode on mount
  useEffect(() => {
    const checkAccess = async () => {
      const token = getToken();
      
      if (!token) {
        // No token = guest mode
        setAccessMode(ACCESS_MODES.GUEST);
        return;
      }
      
      // Try to access with JWT - endpoint is public but JWT gives full access
      try {
        const response = await axios.get(`${PUBLIC_API_URL}/public/stocks/screen`, {
          params: { limit: 1 },
          headers: { Authorization: `Bearer ${token}` }
        });
        // Success = full access
        setAccessMode(ACCESS_MODES.FULL);
        if (response.data?.total_matching) {
          setTotalAvailable(response.data.total_matching);
        }
      } catch (error) {
        if (error.response?.status === 401) {
          // Invalid token
          removeToken();
          setAccessMode(ACCESS_MODES.GUEST);
        } else if (error.response?.status === 403) {
          // No subscription
          setAccessMode(ACCESS_MODES.NO_SUBSCRIPTION);
        } else {
          // Other error - treat as guest
          setAccessMode(ACCESS_MODES.GUEST);
        }
      }
    };
    
    checkAccess();
  }, [user]);

  // Fetch sectors dynamically - use public endpoint if preview mode
  useEffect(() => {
    const fetchSectors = async () => {
      // Wait for access mode to be determined
      if (accessMode === null) return;
      
      try {
        if (accessMode === ACCESS_MODES.FULL) {
          // Use protected endpoint with auth
          const data = await stocksAPI.getSectors(selectedMarket !== 'all' ? selectedMarket : undefined);
          const sectorList = Array.isArray(data) ? data : (data.sectors || []);
          setSectors(sectorList);
        } else {
          // Use public endpoint for preview
          const params = selectedMarket !== 'all' ? { country: selectedMarket } : {};
          const response = await axios.get(`${PUBLIC_API_URL}/public/stocks/sectors`, { params });
          const sectorList = Array.isArray(response.data) ? response.data : (response.data?.sectors || []);
          setSectors(sectorList);
        }
        // Reset sector filter if current selection doesn't exist in new market
        if (selectedSector !== 'all' && !sectors.includes(selectedSector)) {
          setSelectedSector('all');
        }
      } catch (err) {
        console.error('Failed to fetch sectors:', err);
        setSectors([]);
      }
    };
    fetchSectors();
  }, [selectedMarket, accessMode]);

  // Fetch inflation rates from API with 24h cache
  useEffect(() => {
    const fetchInflationRates = async () => {
      // Check cache first
      const cachedRates = localStorage.getItem(INFLATION_CACHE_KEY);
      const cacheTime = localStorage.getItem(INFLATION_CACHE_TIME_KEY);
      
      if (cachedRates && cacheTime) {
        const elapsed = Date.now() - parseInt(cacheTime, 10);
        const parsed = JSON.parse(cachedRates);
        // Only use cache if it has actual data (not empty object)
        if (elapsed < CACHE_DURATION_MS && Object.keys(parsed).length > 0) {
          setInflationRates(parsed);
          return;
        }
      }
      
      // Fetch from API for each market
      const countryCodes = ['VN', 'IN', 'PH', 'ID', 'PL', 'KR'];
      const marketMapping = { VN: 'VN', IN: ['NSE', 'IN'], PH: ['PSE', 'PH'], ID: ['JK', 'ID'], PL: ['WSE', 'PL'], KR: ['KRX', 'KR'] };
      
      try {
        const newRates = { ...FALLBACK_INFLATION };
        
        const results = await Promise.all(
          countryCodes.map(code => 
            analysisAPI.getInflation(code)
              .then(data => ({ code, rate: data.current }))
              .catch(() => ({ code, rate: null }))
          )
        );
        
        results.forEach(({ code, rate }) => {
          if (rate !== null) {
            const mappings = marketMapping[code];
            if (Array.isArray(mappings)) {
              mappings.forEach(m => { newRates[m] = rate; });
            } else {
              newRates[mappings] = rate;
            }
          }
        });
        
        // Update state and cache
        setInflationRates(newRates);
        localStorage.setItem(INFLATION_CACHE_KEY, JSON.stringify(newRates));
        localStorage.setItem(INFLATION_CACHE_TIME_KEY, Date.now().toString());
      } catch (err) {
        console.error('Failed to fetch inflation rates, using fallback:', err);
        // Keep fallback rates already set in state
      }
    };
    
    fetchInflationRates();
  }, []);

  // Memoized calculation functions using dynamic inflation rates
  const calculateRealPE = useCallback((nominalPE, market) => {
    if (!nominalPE || nominalPE <= 0) return null;
    const localInflation = inflationRates[market] || 3.0;
    return nominalPE * (1 + US_INFLATION / 100) / (1 + localInflation / 100);
  }, [inflationRates]);

  const calculateRealROE = useCallback((nominalROE, market) => {
    if (nominalROE === null || nominalROE === undefined) return null;
    const localInflation = inflationRates[market] || 3.0;
    const inflationPremium = localInflation - US_INFLATION;
    return nominalROE - inflationPremium;
  }, [inflationRates]);

  // Helper function to fetch preview data from public endpoint
  const fetchPreviewStocks = async (market = selectedMarket) => {
    const params = { limit: previewLimit };
    // Apply country filter in preview mode if selected
    if (market !== 'all') {
      params.country = market;
    }
    const response = await axios.get(`${PUBLIC_API_URL}/public/stocks/screen`, { params });
    const data = response.data;
    const mappedStocks = (data.stocks || []).map(s => ({
      symbol: s.symbol,
      name: s.name,
      market: s.market,
      country: s.country,
      sector: s.sector,
      pe: s.pe_ratio,
      pb: s.pb_ratio,
      roe: s.roe,
      real_roe: s.real_roe ?? null,
      marketCap: s.market_cap,
      price: s.price,
      currency: s.currency,
      valoramap_score: s.valoramap_score ?? null,
      valoramap_score_basis: s.valoramap_score_basis ?? null,
      liquidity_score: s.liquidity_score ?? null,
      // Scoring fields — preserve undefined vs null distinction for graceful degradation
      altman_zscore: 'altman_zscore' in s ? s.altman_zscore : undefined,
      altman_zone: 'altman_zone' in s ? s.altman_zone : undefined,
      piotroski_fscore: 'piotroski_fscore' in s ? s.piotroski_fscore : undefined,
      piotroski_coverage: 'piotroski_coverage' in s ? s.piotroski_coverage : undefined,
      piotroski_zone: 'piotroski_zone' in s ? s.piotroski_zone : undefined,
      beneish_mscore: 'beneish_mscore' in s ? s.beneish_mscore : undefined,
      beneish_coverage: 'beneish_coverage' in s ? s.beneish_coverage : undefined,
      beneish_zone: 'beneish_zone' in s ? s.beneish_zone : undefined,
      scoring_exclusion_reason: 'scoring_exclusion_reason' in s ? s.scoring_exclusion_reason : undefined,
    }));
    setStocks(mappedStocks);
    setTotalStocks(previewLimit);
    setTotalAvailable(data.total_matching || data.count || 6513);
    setTotalPages(1);
  };

  // Fetch stocks from API - uses different endpoints based on access mode
  const fetchStocks = useCallback(async () => {
    // Wait for access mode to be determined
    if (accessMode === null) return;
    
    setLoading(true);
    setError(null);

    try {
      // Preview mode: use public endpoint with limited results
      if (isPreviewMode) {
        await fetchPreviewStocks();
        setLoading(false);
        return;
      }

      // Full mode: use protected endpoint with all features
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        sort: sortConfig.key,
        order: sortConfig.direction
      };

      if (selectedMarket !== 'all') params.market = selectedMarket;
      if (selectedSector !== 'all') params.sector = selectedSector;
      if (searchQuery) params.search = searchQuery;
      if (peRange.min) params.pe_min = parseFloat(peRange.min);
      if (peRange.max) params.pe_max = parseFloat(peRange.max);
      if (pbRange.min) params.pb_min = parseFloat(pbRange.min);
      if (pbRange.max) params.pb_max = parseFloat(pbRange.max);
      if (roeRange.min) params.roe_min = parseFloat(roeRange.min);
      if (roeRange.max) params.roe_max = parseFloat(roeRange.max);
      if (scoreMin && scoreMin !== 'all') params.score_min = parseInt(scoreMin);
      if (liquidityMin && liquidityMin !== 'all') params.liquidity_min = parseInt(liquidityMin);
      if (hasAdrFilter !== 'all') params.has_adr_filter = hasAdrFilter === 'yes';
      // Catalyst FTSE eligible filter
      if (catalystFtseEligible) params.catalyst_ftse_eligible = true;
      // Server-side Real ROE filtering (backend handles conversion)
      // NOTE: Backend may not filter properly yet, client-side fallback below
      if (realRoeMin) params.real_roe_min = parseFloat(realRoeMin);
      
      // Request more stocks if client-side filtering is needed
      // Real ROE needs client-side fallback until backend is fixed
      const needsClientFiltering = realPeMax || realRoeMin;
      if (needsClientFiltering) {
        params.limit = 500; // Fetch more to filter client-side
        params.page = 1;
      }

      const data = await stocksAPI.getStocks(params);
      let filteredStocks = data.stocks || [];
      
      // Client-side filtering for Real P/E (requires inflation adjustment)
      if (realPeMax) {
        const maxRealPE = parseFloat(realPeMax);
        filteredStocks = filteredStocks.filter(stock => {
          const realPE = calculateRealPE(stock.pe, stock.market);
          return realPE !== null && realPE <= maxRealPE;
        });
      }
      
      // Client-side fallback for Real ROE (backend doesn't filter yet)
      if (realRoeMin) {
        const minRealROE = parseFloat(realRoeMin) / 100; // Convert 8 to 0.08
        filteredStocks = filteredStocks.filter(stock => {
          const realROE = stock.real_roe ?? null;
          return realROE !== null && realROE >= minRealROE;
        });
      }
      
      // Apply pagination client-side if filtering was done
      const totalFiltered = filteredStocks.length;
      if (needsClientFiltering) {
        const startIndex = (currentPage - 1) * itemsPerPage;
        filteredStocks = filteredStocks.slice(startIndex, startIndex + itemsPerPage);
      }
      
      setStocks(filteredStocks);
      setTotalStocks(needsClientFiltering ? totalFiltered : (data.total || 0));
      setTotalPages(needsClientFiltering ? Math.ceil(totalFiltered / itemsPerPage) : (data.pages || 1));
    } catch (err) {
      console.error('Error fetching stocks:', err);
      
      // Handle 403 - fallback to preview mode
      if (err.response?.status === 403 || err.isAccessDenied) {
        try {
          await fetchPreviewStocks();
          // Set access mode AFTER successful fetch to avoid re-triggering useEffect
          setAccessMode(ACCESS_MODES.NO_SUBSCRIPTION);
        } catch (fallbackErr) {
          console.error('Fallback also failed:', fallbackErr);
          setError('Erreur lors du chargement des données');
        }
        setLoading(false);
        return;
      }
      
      // Handle 401 - token invalid, switch to guest mode
      if (err.response?.status === 401) {
        removeToken();
        try {
          await fetchPreviewStocks();
          // Set access mode AFTER successful fetch to avoid re-triggering useEffect
          setAccessMode(ACCESS_MODES.GUEST);
        } catch (fallbackErr) {
          console.error('Fallback also failed:', fallbackErr);
          setError('Erreur lors du chargement des données');
        }
        setLoading(false);
        return;
      }
      
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [currentPage, selectedMarket, selectedSector, searchQuery, peRange, pbRange, roeRange, realPeMax, realRoeMin, scoreMin, liquidityMin, hasAdrFilter, sortConfig, calculateRealPE, itemsPerPage, accessMode, isPreviewMode]);
  
  // Trigger initial fetch when access mode is determined
  // Note: stocks.length check prevents re-fetch after 403/401 fallback which already loaded data
  useEffect(() => {
    if (accessMode !== null && stocks.length === 0) {
      fetchStocks();
    }
  }, [accessMode]);
  
  // Trigger fetch when filters/pagination change (only in full mode)
  useEffect(() => {
    // Re-fetch on filter changes if we're in full mode
    if (accessMode === ACCESS_MODES.FULL) {
      fetchStocks();
    }
  }, [currentPage, selectedMarket, selectedSector, searchQuery, sortConfig, peRange, pbRange, roeRange, realPeMax, realRoeMin, scoreMin, liquidityMin, hasAdrFilter, catalystFtseEligible]);

  // Fetch accounting quality for visible stocks (batch)
  useEffect(() => {
    const fetchQualityForStocks = async () => {
      if (!stocks || stocks.length === 0) return;
      
      // Only fetch for stocks not in cache
      const toFetch = stocks.filter(s => !qualityCache[`${s.market}-${s.symbol}`]);
      
      if (toFetch.length === 0) return;
      
      // Fetch in parallel (limit to 5 at a time to avoid overload)
      const batchSize = 5;
      for (let i = 0; i < Math.min(toFetch.length, 15); i += batchSize) {
        const batch = toFetch.slice(i, i + batchSize);
        const results = await Promise.all(
          batch.map(stock => 
            analysisAPI.getAccountingQuality(stock.market, stock.symbol)
              .then(data => ({ key: `${stock.market}-${stock.symbol}`, quality: data?.accounting_quality || null }))
              .catch(() => ({ key: `${stock.market}-${stock.symbol}`, quality: null }))
          )
        );
        
        setQualityCache(prev => {
          const newCache = { ...prev };
          results.forEach(r => { newCache[r.key] = r.quality; });
          return newCache;
        });
      }
    };
    
    fetchQualityForStocks();
  }, [stocks]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedMarket, selectedSector, searchQuery, peRange, pbRange, roeRange, realPeMax, realRoeMin, qualityFilter, scoreMin, liquidityMin, hasAdrFilter, catalystFtseEligible]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Client-side sorted stocks for real metrics columns and quality
  const sortedStocks = useMemo(() => {
    let result = [...stocks];
    
    // Client-side quality filter
    if (qualityFilter && qualityFilter !== 'all') {
      result = result.filter(stock => {
        const quality = qualityCache[`${stock.market}-${stock.symbol}`];
        const { status } = analyzeQualityFlags(quality);
        
        if (qualityFilter === 'clean') return status === 'ok';
        if (qualityFilter === 'warning') return status === 'warning' || status === 'critical';
        if (qualityFilter === 'critical') return status === 'critical';
        return true;
      });
    }
    
    // Apply sorting for real metrics columns
    if (sortConfig.key === 'realPE' || sortConfig.key === 'realROE') {
      result.sort((a, b) => {
        let aVal, bVal;
        
        if (sortConfig.key === 'realPE') {
          aVal = calculateRealPE(a.pe, a.market);
          bVal = calculateRealPE(b.pe, b.market);
        } else if (sortConfig.key === 'realROE') {
          aVal = calculateRealROE(a.roe, a.market);
          bVal = calculateRealROE(b.roe, b.market);
        }
        
        if (aVal === null && bVal === null) return 0;
        if (aVal === null) return 1;
        if (bVal === null) return -1;
        
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      });
    }
    
    return result;
  }, [stocks, sortConfig, qualityFilter, qualityCache, calculateRealPE, calculateRealROE]);

  // Apply scoring filters client-side (graceful degradation if fields not yet in API)
  // TODO post-launch: move filtering to backend query params for consistent pagination
  const scoringFilteredStocks = useMemo(() => {
    return sortedStocks.filter(stock => {
      // Hide financials filter
      if (hideFinancials) {
        if (stock.altman_zone === 'not_applicable_financials' ||
            stock.piotroski_zone === 'not_applicable_financials' ||
            stock.beneish_zone === 'not_applicable_financials' ||
            stock.scoring_exclusion_reason === 'financial_sector') {
          return false;
        }
      }
      
      // Hide insufficient data filter
      // Rule: hide if at least 2 of 3 models are insufficient/null (excluding financial exclusion)
      if (hideInsufficientData) {
        const isFinancial = stock.scoring_exclusion_reason === 'financial_sector' ||
                           stock.altman_zone === 'not_applicable_financials';
        
        if (!isFinancial) {
          const insufficientCount = [
            stock.piotroski_coverage === 'insufficient' || stock.piotroski_fscore == null,
            stock.beneish_coverage === 'insufficient' || stock.beneish_mscore == null,
            stock.altman_zscore == null
          ].filter(Boolean).length;
          
          if (insufficientCount >= 2) return false;
        }
      }
      
      return true;
    });
  }, [sortedStocks, hideFinancials, hideInsufficientData]);

  // Calculate if quality column should be shown (hide if >80% N/A)
  const showQualityColumn = useMemo(() => {
    if (Object.keys(qualityCache).length === 0) return true; // Show while loading
    
    const stocksWithQuality = sortedStocks.filter(stock => {
      const quality = qualityCache[`${stock.market}-${stock.symbol}`];
      if (!quality) return false;
      const { status } = analyzeQualityFlags(quality);
      return status !== 'unknown';
    });
    
    // Show column if at least 20% have valid quality data
    return stocksWithQuality.length >= sortedStocks.length * 0.2;
  }, [sortedStocks, qualityCache]);

  const toggleWatchlist = async (symbol) => {
    setWatchlist(prev =>
      prev.includes(symbol)
        ? prev.filter(s => s !== symbol)
        : [...prev, symbol]
    );
  };

  const clearFilters = () => {
    setSelectedMarket('all');
    setSelectedSector('all');
    setPeRange({ min: '', max: '' });
    setPbRange({ min: '', max: '' });
    setRoeRange({ min: '', max: '' });
    setRealPeMax('');
    setRealRoeMin('');
    setQualityFilter('all');
    setScoreMin('');
    setLiquidityMin('');
    setHasAdrFilter('all');
    setSearchQuery('');
  };

  const getMarketFlag = (code) => {
    const market = markets.find(m => m.code === code);
    return market?.flag || '';
  };

  const handleExportCSV = () => {
    if (!stocks || stocks.length === 0) {
      alert('Aucune donnée à exporter');
      return;
    }

    const headers = ['Symbol', 'Name', 'Market', 'Price', 'Change %', 'P/E', 'P/B', 'ROE %', 'Sector'];
    const rows = stocks.map(s => [
      s.symbol || '',
      `"${(s.name || '').replace(/"/g, '""')}"`,
      s.market || '',
      s.price ?? '',
      s.change ?? '',
      s.pe ? s.pe.toFixed(2) : '',
      s.pb ? s.pb.toFixed(2) : '',
      s.roe ? s.roe.toFixed(2) : '',
      s.sector || ''
    ]);

    const csv = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `valoramap_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="pt-20 pb-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
            Screener Marchés Émergents
          </h1>
          <p className="text-gray-400">
            {totalStocks} actions trouvées
          </p>
        </div>

        {/* Macro Context Widget */}
        <MacroWidget 
          selectedMarket={selectedMarket} 
          onMarketSelect={setSelectedMarket}
        />

        {/* Search and Filter Bar */}
        <Card className="bg-white/5 border-white/10 mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search Input */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Rechercher par symbole ou nom..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                />
              </div>

              {/* Quick Filters */}
              <div className="flex flex-wrap gap-3">
                <Select value={selectedMarket} onValueChange={setSelectedMarket}>
                  <SelectTrigger className="w-[150px] bg-white/5 border-white/10 text-white">
                    <SelectValue placeholder="Marché" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les marchés</SelectItem>
                    {markets.map(market => (
                      <SelectItem key={market.code} value={market.code}>
                        {market.flag} {market.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={selectedSector} onValueChange={setSelectedSector}>
                  <SelectTrigger className="w-[180px] bg-white/5 border-white/10 text-white">
                    <SelectValue placeholder="Secteur" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-700">
                    <SelectItem value="all" className="text-white hover:bg-white/10">Tous les secteurs</SelectItem>
                    {sectors.map(sector => (
                      <SelectItem key={sector} value={sector} className="text-white hover:bg-white/10">
                        {SECTOR_TRANSLATIONS[sector] || sector}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Advanced Filters Button - Hidden in preview mode */}
                {!isPreviewMode && (
                  <Button
                    variant="outline"
                    onClick={() => setShowFilters(!showFilters)}
                    className="border-white/10 text-gray-300 hover:bg-white/5"
                    data-testid="advanced-filters-btn"
                  >
                    <SlidersHorizontal className="w-4 h-4 mr-2" />
                    Filtres avancés
                  </Button>
                )}

                {/* Export CSV Button - Hidden in preview mode */}
                {!isPreviewMode && (
                  <Button
                    variant="outline"
                    onClick={handleExportCSV}
                    className="border-white/10 text-gray-300 hover:bg-white/5"
                    data-testid="export-csv-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                )}
              </div>
            </div>

            {/* Advanced Filters Panel */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-white/10">
                {/* Toggle for Real Metrics columns */}
                <div className="mb-4 flex items-center gap-3">
                  <button
                    onClick={() => setShowRealMetrics(!showRealMetrics)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                      showRealMetrics 
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' 
                        : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'
                    }`}
                  >
                    <BarChart3 className="w-4 h-4" />
                    {showRealMetrics ? 'Masquer Real P/E & ROE' : 'Afficher Real P/E & ROE'}
                  </button>
                  <span className="text-xs text-gray-500">Métriques ajustées pour l'inflation</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* P/E Range */}
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">
                      <MetricLabel metric="pe" label="P/E Ratio" />
                    </label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        placeholder="Min"
                        value={peRange.min}
                        onChange={(e) => setPeRange(prev => ({ ...prev, min: e.target.value }))}
                        className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                      />
                      <Input
                        type="number"
                        placeholder="Max"
                        value={peRange.max}
                        onChange={(e) => setPeRange(prev => ({ ...prev, max: e.target.value }))}
                        className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                      />
                    </div>
                  </div>

                  {/* P/B Range */}
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">
                      <MetricLabel metric="pb" label="P/B Ratio" />
                    </label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        placeholder="Min"
                        value={pbRange.min}
                        onChange={(e) => setPbRange(prev => ({ ...prev, min: e.target.value }))}
                        className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                      />
                      <Input
                        type="number"
                        placeholder="Max"
                        value={pbRange.max}
                        onChange={(e) => setPbRange(prev => ({ ...prev, max: e.target.value }))}
                        className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                      />
                    </div>
                  </div>

                  {/* ROE Range */}
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">
                      <MetricLabel metric="roe" label="ROE (%)" />
                    </label>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        placeholder="Min"
                        value={roeRange.min}
                        onChange={(e) => setRoeRange(prev => ({ ...prev, min: e.target.value }))}
                        className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                      />
                      <Input
                        type="number"
                        placeholder="Max"
                        value={roeRange.max}
                        onChange={(e) => setRoeRange(prev => ({ ...prev, max: e.target.value }))}
                        className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                      />
                    </div>
                  </div>

                  {/* Real P/E Max (Phase 2) */}
                  <div>
                    <label className="text-sm text-emerald-400 mb-2 block flex items-center gap-1">
                      <BarChart3 className="w-4 h-4" />
                      <MetricLabel metric="realPE" label="Real P/E (max)" />
                    </label>
                    <Select value={realPeMax || "all"} onValueChange={(val) => setRealPeMax(val === "all" ? "" : val)}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white border-emerald-500/30" data-testid="real-pe-filter">
                        <SelectValue placeholder="Tous" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-gray-700">
                        <SelectItem value="all" className="text-white hover:bg-white/10">Tous</SelectItem>
                        <SelectItem value="10" className="text-emerald-400 hover:bg-white/10">Real P/E ≤ 10 (Très bon marché)</SelectItem>
                        <SelectItem value="12" className="text-emerald-400 hover:bg-white/10">Real P/E ≤ 12</SelectItem>
                        <SelectItem value="15" className="text-yellow-400 hover:bg-white/10">Real P/E ≤ 15 (Raisonnable)</SelectItem>
                        <SelectItem value="20" className="text-orange-400 hover:bg-white/10">Real P/E ≤ 20</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Real ROE Min (Phase 2) */}
                  <div>
                    <label className="text-sm text-emerald-400 mb-2 block flex items-center gap-1">
                      <BarChart3 className="w-4 h-4" />
                      <MetricLabel metric="realROE" label="Real ROE (min)" />
                    </label>
                    <Select value={realRoeMin || "all"} onValueChange={(val) => setRealRoeMin(val === "all" ? "" : val)}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white border-emerald-500/30" data-testid="real-roe-filter">
                        <SelectValue placeholder="Tous" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-gray-700">
                        <SelectItem value="all" className="text-white hover:bg-white/10">Tous</SelectItem>
                        <SelectItem value="15" className="text-emerald-400 hover:bg-white/10">Real ROE ≥ 15% (Excellent)</SelectItem>
                        <SelectItem value="12" className="text-emerald-400 hover:bg-white/10">Real ROE ≥ 12%</SelectItem>
                        <SelectItem value="10" className="text-yellow-400 hover:bg-white/10">Real ROE ≥ 10% (Bon)</SelectItem>
                        <SelectItem value="8" className="text-orange-400 hover:bg-white/10">Real ROE ≥ 8%</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Quality Filter (Phase 2 - Red Flags) - Only show if quality data available */}
                  {showQualityColumn && (
                    <div>
                      <label className="text-sm text-red-400 mb-2 block flex items-center gap-1">
                        <ShieldAlert className="w-4 h-4" />
                        Qualité Comptable
                      </label>
                      <Select value={qualityFilter} onValueChange={setQualityFilter}>
                        <SelectTrigger className="bg-white/5 border-white/10 text-white border-red-500/30" data-testid="quality-filter">
                          <SelectValue placeholder="Tous" />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-900 border-gray-700">
                          <SelectItem value="all" className="text-white hover:bg-white/10">Tous</SelectItem>
                          <SelectItem value="clean" className="text-emerald-400 hover:bg-white/10">Sans alerte (OK)</SelectItem>
                          <SelectItem value="warning" className="text-amber-400 hover:bg-white/10">Avec alertes</SelectItem>
                          <SelectItem value="critical" className="text-red-400 hover:bg-white/10">Alertes critiques seulement</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Score Min */}
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">
                      Score Valorisation (min)
                    </label>
                    <Select value={scoreMin || "all"} onValueChange={(val) => setScoreMin(val === "all" ? "" : val)}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="score-filter">
                        <SelectValue placeholder="Tous" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-gray-700">
                        <SelectItem value="all" className="text-white hover:bg-white/10">Tous</SelectItem>
                        <SelectItem value="80" className="text-emerald-400 hover:bg-white/10">Score ≥ 80 (Sous-valorisé)</SelectItem>
                        <SelectItem value="60" className="text-yellow-400 hover:bg-white/10">Score ≥ 60 (Équilibré)</SelectItem>
                        <SelectItem value="40" className="text-orange-400 hover:bg-white/10">Score ≥ 40</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Liquidity Score Min */}
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block flex items-center gap-1">
                      <Droplets className="w-4 h-4" />
                      Liquidité (min)
                    </label>
                    <Select value={liquidityMin || "all"} onValueChange={(val) => setLiquidityMin(val === "all" ? "" : val)}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="liquidity-filter">
                        <SelectValue placeholder="Tous" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-gray-700">
                        <SelectItem value="all" className="text-white hover:bg-white/10">Tous</SelectItem>
                        <SelectItem value="75" className="text-emerald-400 hover:bg-white/10">Score ≥ 75 (Très liquide)</SelectItem>
                        <SelectItem value="50" className="text-yellow-400 hover:bg-white/10">Score ≥ 50 (Modéré)</SelectItem>
                        <SelectItem value="25" className="text-orange-400 hover:bg-white/10">Score ≥ 25 (Faible)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* ADR Filter */}
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block flex items-center gap-1">
                      <Globe className="w-4 h-4" />
                      ADR (US)
                    </label>
                    <Select value={hasAdrFilter} onValueChange={setHasAdrFilter}>
                      <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="adr-filter">
                        <SelectValue placeholder="Tous" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-gray-700">
                        <SelectItem value="all" className="text-white hover:bg-white/10">Tous</SelectItem>
                        <SelectItem value="yes" className="text-emerald-400 hover:bg-white/10">Avec ADR</SelectItem>
                        <SelectItem value="no" className="text-gray-400 hover:bg-white/10">Sans ADR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Clear Filters */}
                  <div className="flex items-end">
                    <Button
                      variant="ghost"
                      onClick={clearFilters}
                      className="text-gray-400 hover:text-white"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Réinitialiser
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Preview Mode Banner */}
        {isPreviewMode && accessMode !== null && (
          <Card className="bg-gradient-to-r from-emerald-900/30 to-emerald-800/20 border-emerald-500/30 mb-6" data-testid="preview-banner">
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-emerald-500/20 rounded-lg">
                    <Lock className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-lg">
                      {accessMode === ACCESS_MODES.GUEST 
                        ? `${previewLimit} actions sur ${totalAvailable.toLocaleString('fr-FR')} — Mode aperçu`
                        : 'Votre abonnement n\'est pas actif'
                      }
                    </h3>
                    <p className="text-gray-400 text-sm mt-1">
                      {accessMode === ACCESS_MODES.GUEST 
                        ? 'Créez un compte gratuit pour accéder à l\'ensemble des données et fonctionnalités avancées.'
                        : 'Débloquez l\'accès complet au screener, aux filtres avancés et à l\'export des données.'
                      }
                    </p>
                  </div>
                </div>
                <Link 
                  to={accessMode === ACCESS_MODES.GUEST ? '/login' : '/pricing'}
                  className="shrink-0"
                >
                  <Button className="bg-emerald-500 hover:bg-emerald-600 text-white gap-2" data-testid="preview-cta-btn">
                    {accessMode === ACCESS_MODES.GUEST ? 'Créer un compte' : 'Voir les offres'}
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Results Table */}
        <Card className="bg-white/5 border-white/10 overflow-hidden">
          {/* Scoring Filters - Visible above table */}
          {!isPreviewMode && (
            <div className="px-4 py-3 border-b border-white/10 flex items-center gap-6 flex-wrap">
              <span className="text-sm text-gray-400">Filtres scoring :</span>
              <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hideFinancials}
                  onChange={(e) => {
                    setHideFinancials(e.target.checked);
                    setCurrentPage(1);
                  }}
                  className="w-4 h-4 rounded border-gray-600 bg-white/5 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-0"
                />
                Masquer les financières (N/A)
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hideInsufficientData}
                  onChange={(e) => {
                    setHideInsufficientData(e.target.checked);
                    setCurrentPage(1);
                  }}
                  className="w-4 h-4 rounded border-gray-600 bg-white/5 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-0"
                />
                Masquer les tickers avec données insuffisantes
              </label>
              {(hideFinancials || hideInsufficientData) && (
                <span className="text-xs text-gray-500">
                  ({scoringFilteredStocks.length} sur {stocks.length} affichés)
                </span>
              )}
            </div>
          )}
          {loading ? (
            <div className="p-12 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
            </div>
          ) : error ? (
            <div className="p-12 text-center">
              <p className="text-red-400">{error}</p>
              <Button onClick={fetchStocks} className="mt-4 bg-emerald-500 hover:bg-emerald-600">
                Réessayer
              </Button>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full table-fixed">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left p-2 sm:p-3 text-sm font-medium text-gray-400 w-[140px] sm:w-[180px]">
                        <button onClick={() => handleSort('symbol')} className="flex items-center gap-1 hover:text-white">
                          Action
                          <ArrowUpDown className="w-3 h-3" />
                        </button>
                      </th>
                      <th className="text-left p-2 sm:p-3 text-sm font-medium text-gray-400 w-[70px]">Marché</th>
                      <th className="text-right p-2 sm:p-3 text-sm font-medium text-gray-400 w-[90px]">
                        <button onClick={() => handleSort('price')} className="flex items-center gap-1 hover:text-white ml-auto">
                          Prix
                          <ArrowUpDown className="w-3 h-3" />
                        </button>
                      </th>
                      <th className="text-right p-2 sm:p-3 text-sm font-medium text-gray-400 w-[70px]">
                        <button onClick={() => handleSort('change')} className="flex items-center gap-1 hover:text-white ml-auto">
                          Var.
                          <ArrowUpDown className="w-3 h-3" />
                        </button>
                      </th>
                      <th className="text-right p-2 sm:p-3 text-sm font-medium text-gray-400 w-[55px]">
                        <button onClick={() => handleSort('pe')} className="flex items-center gap-1 hover:text-white ml-auto">
                          <MetricLabel metric="pe" label="P/E" />
                          <ArrowUpDown className="w-3 h-3" />
                        </button>
                      </th>
                      {showRealMetrics && (
                        <th className="text-right p-2 sm:p-3 text-sm font-medium text-emerald-400 w-[70px]">
                          <button onClick={() => handleSort('realPE')} className="flex items-center gap-1 hover:text-emerald-300 ml-auto" title="P/E ajusté pour l'inflation">
                            <MetricLabel metric="realPE" label="Real P/E" />
                            <ArrowUpDown className="w-3 h-3" />
                          </button>
                        </th>
                      )}
                      <th className="text-right p-2 sm:p-3 text-sm font-medium text-gray-400 w-[50px]">
                        <button onClick={() => handleSort('pb')} className="flex items-center gap-1 hover:text-white ml-auto">
                          <MetricLabel metric="pb" label="P/B" />
                          <ArrowUpDown className="w-3 h-3" />
                        </button>
                      </th>
                      <th className="text-right p-2 sm:p-3 text-sm font-medium text-gray-400 w-[55px]">
                        <button onClick={() => handleSort('roe')} className="flex items-center gap-1 hover:text-white ml-auto">
                          <MetricLabel metric="roe" label="ROE" />
                          <ArrowUpDown className="w-3 h-3" />
                        </button>
                      </th>
                      {showRealMetrics && (
                        <th className="text-right p-2 sm:p-3 text-sm font-medium text-emerald-400 w-[75px]">
                          <button onClick={() => handleSort('realROE')} className="flex items-center gap-1 hover:text-emerald-300 ml-auto" title="ROE ajusté pour l'inflation">
                            <MetricLabel metric="realROE" label="Real ROE" />
                            <ArrowUpDown className="w-3 h-3" />
                          </button>
                        </th>
                      )}
                      <th className="text-right p-2 sm:p-3 text-sm font-medium text-gray-400 w-[75px]">
                        <button onClick={() => handleSort('marketCap')} className="flex items-center gap-1 hover:text-white ml-auto">
                          <MetricLabel metric="marketCap" label="Cap." />
                          <ArrowUpDown className="w-3 h-3" />
                        </button>
                      </th>
                      {/* Scoring columns: Altman Z, Piotroski F, Beneish M */}
                      <th className="text-center p-2 sm:p-3 text-sm font-medium text-gray-400" style={{ minWidth: '90px' }}>
                        <TooltipProvider delayDuration={0}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="flex items-center gap-1 justify-center cursor-help">
                                Altman Z
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-[280px] bg-[#1a1a2e] border-white/20 text-gray-200">
                              <p className="text-xs">Z''-Score d'Altman (marchés émergents). Mesure le risque de faillite. &gt;2.6 sûr, 1.1-2.6 zone grise, &lt;1.1 détresse.</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </th>
                      <th className="text-center p-2 sm:p-3 text-sm font-medium text-gray-400" style={{ minWidth: '90px' }}>
                        <TooltipProvider delayDuration={0}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="flex items-center gap-1 justify-center cursor-help">
                                Piotroski F
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-[280px] bg-[#1a1a2e] border-white/20 text-gray-200">
                              <p className="text-xs">F-Score de Piotroski. 9 signaux de qualité comptable. 7-9 solide, 4-6 neutre, 0-3 faible.</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </th>
                      <th className="text-center p-2 sm:p-3 text-sm font-medium text-gray-400" style={{ minWidth: '90px' }}>
                        <TooltipProvider delayDuration={0}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="flex items-center gap-1 justify-center cursor-help">
                                Beneish M
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-[280px] bg-[#1a1a2e] border-white/20 text-gray-200">
                              <p className="text-xs">M-Score de Beneish. Probabilité de manipulation des résultats. &lt;-2.22 faible risque, &gt;=-2.22 risque élevé.</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </th>
                      <th className="text-center p-2 sm:p-3 text-sm font-medium text-[#00ff88] w-[60px]">
                        {isPreviewMode ? (
                          <span className="flex items-center gap-1 justify-center text-gray-500" title="Score verrouillé">
                            <Lock className="w-3 h-3" />
                            Score
                          </span>
                        ) : (
                          <TooltipProvider delayDuration={0}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button onClick={() => handleSort('valoramap_score')} className="flex items-center gap-1 hover:text-[#00ff88]/80 mx-auto">
                                  <ArrowUpDown className="w-3 h-3" />
                                  Score
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-[250px] bg-[#1a1a2e] border-white/20 text-gray-200">
                                <p className="text-xs">VALORAMAP Score (0-100). Combine solidité financière, fiabilité comptable, valorisation relative, catalyseur actif et liquidité.</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </th>
                      <th className="text-center p-2 sm:p-3 text-sm font-medium text-gray-400 w-[50px]">
                        <TooltipProvider delayDuration={0}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button onClick={() => handleSort('liquidity_score')} className="flex items-center gap-1 hover:text-white mx-auto">
                                <Droplets className="w-3 h-3" />
                                Liq.
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-[250px] bg-[#1a1a2e] border-white/20 text-gray-200">
                              <p className="text-xs">Score de liquidité (0-100). Plus le score est élevé, plus le titre est facile à acheter/vendre.</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </th>
                      {showQualityColumn && (
                        <th className="text-center p-2 sm:p-3 text-sm font-medium text-red-400 w-[50px]">
                          <span className="flex items-center gap-1 justify-center" title="Qualité Comptable">
                            <ShieldAlert className="w-3 h-3" />
                          </span>
                        </th>
                      )}
                      <th className="text-center p-2 sm:p-3 text-sm font-medium text-gray-400 w-[50px]">
                        <TooltipProvider delayDuration={0}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="flex items-center gap-1 justify-center cursor-help">
                                <Globe className="w-3 h-3" />
                                ADR
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-[280px] bg-[#1a1a2e] border-white/20 text-gray-200">
                              <p className="text-xs">ADR disponible : le titre est accessible via un American Depositary Receipt sur une bourse US ou européenne (✓ = oui).</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </th>
                      <th className="text-center p-2 sm:p-3 text-sm font-medium text-gray-400 w-[70px]">
                        <TooltipProvider delayDuration={0}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="flex items-center gap-1 justify-center cursor-help">
                                <Calendar className="w-3 h-3" />
                                Résultats
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-[250px] bg-[#1a1a2e] border-white/20 text-gray-200">
                              <p className="text-xs">Date de publication des prochains résultats financiers de l'entreprise.</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </th>
                      {/* WATCHLIST COLUMN HIDDEN FOR LAUNCH
                      <th className="text-center p-2 sm:p-3 text-sm font-medium text-gray-400 w-[40px]">
                        <Star className="w-3 h-3 mx-auto" />
                      </th>
                      */}
                    </tr>
                  </thead>
                  <tbody>
                    {scoringFilteredStocks.map((stock) => (
                      <tr 
                        key={`${stock.market}-${stock.symbol}`} 
                        className="border-b border-white/5 hover:bg-white/5 transition-colors"
                      >
                        <td className="p-2 sm:p-3">
                          <Link to={`/stock/${stock.market}-${stock.symbol}`} className="block">
                            <div className="font-medium text-white hover:text-emerald-400 transition-colors text-sm">
                              {stock.symbol}
                            </div>
                            <div className="text-xs text-gray-500 truncate max-w-[130px] sm:max-w-[160px]">
                              {stock.name}
                            </div>
                          </Link>
                        </td>
                        <td className="p-2 sm:p-3">
                          <span className="text-xs text-gray-400">
                            {getMarketFlag(stock.market)} {stock.market}
                          </span>
                        </td>
                        <td className="p-2 sm:p-3 text-right text-white font-medium text-sm">
                          {formatCurrency(stock.price, stock.currency)}
                        </td>
                        <td className="p-2 sm:p-3 text-right">
                          <span className={`flex items-center justify-end gap-0.5 text-xs ${
                            stock.change == null ? 'text-gray-500' :
                            stock.change >= 0 ? 'text-emerald-400' : 'text-red-400'
                          }`}>
                            {stock.change == null ? '—' : `${stock.change >= 0 ? '+' : ''}${stock.change.toFixed(1)}%`}
                          </span>
                        </td>
                        <td className="p-2 sm:p-3 text-right">
                          <span className="text-white text-sm">
                            {stock.pe === null || stock.pe === undefined || stock.pe > 200 || stock.pe < -200 
                              ? '—' 
                              : stock.pe.toFixed(1)}
                          </span>
                        </td>
                        {/* Real P/E Column - Hidden by default */}
                        {showRealMetrics && (
                          <td className="p-2 sm:p-3 text-right">
                            {(() => {
                              const realPE = calculateRealPE(stock.pe, stock.market);
                              if (realPE === null || !stock.pe || stock.pe > 200 || stock.pe < -200) return <span className="text-gray-600 text-sm">-</span>;
                              return (
                                <span className={`text-sm font-medium ${realPE < 15 ? 'text-emerald-400' : realPE < 25 ? 'text-amber-400' : 'text-red-400'}`}>
                                  {realPE.toFixed(1)}
                                </span>
                              );
                            })()}
                          </td>
                        )}
                        <td className="p-2 sm:p-3 text-right">
                          <span className="text-white text-sm">
                            {stock.pb === null || stock.pb === undefined || stock.pb > 50 || stock.pb < -50 
                              ? '—' 
                              : stock.pb.toFixed(1)}
                          </span>
                        </td>
                        <td className="p-2 sm:p-3 text-right">
                          <span className="text-emerald-400 text-sm">{stock.roe == null ? '—' : `${stock.roe.toFixed(1)}%`}</span>
                        </td>
                        {/* Real ROE Column - Uses backend real_roe field */}
                        {showRealMetrics && (
                          <td className="p-2 sm:p-3 text-right">
                            {(() => {
                              // Use backend-calculated real_roe if available, otherwise calculate client-side
                              const realROE = stock.real_roe !== null && stock.real_roe !== undefined 
                                ? stock.real_roe * 100  // Backend returns decimal (0.15 = 15%)
                                : calculateRealROE(stock.roe, stock.market);
                              if (realROE === null || stock.roe === null || stock.roe === undefined) return <span className="text-gray-600 text-sm">-</span>;
                              return (
                                <span className={`text-sm font-medium ${realROE > 15 ? 'text-emerald-400' : realROE > 8 ? 'text-amber-400' : 'text-red-400'}`}>
                                  {realROE.toFixed(1)}%
                                </span>
                              );
                            })()}
                          </td>
                        )}
                        <td className="p-2 sm:p-3 text-right text-gray-400 text-sm">
                          {formatMarketCap(stock.marketCap ?? stock.market_cap, stock.currency)}
                        </td>
                        {/* Scoring columns: Altman Z, Piotroski F, Beneish M */}
                        <td className="p-2 sm:p-3 text-center">
                          <ScoringBadge
                            score={stock.altman_zscore}
                            zone={stock.altman_zone}
                            model="altman"
                            exclusionReason={stock.scoring_exclusion_reason}
                          />
                        </td>
                        <td className="p-2 sm:p-3 text-center">
                          <ScoringBadge
                            score={stock.piotroski_fscore}
                            zone={stock.piotroski_zone}
                            coverage={stock.piotroski_coverage}
                            model="piotroski"
                            exclusionReason={stock.scoring_exclusion_reason}
                          />
                        </td>
                        <td className="p-2 sm:p-3 text-center">
                          <ScoringBadge
                            score={stock.beneish_mscore}
                            zone={stock.beneish_zone}
                            coverage={stock.beneish_coverage}
                            model="beneish"
                            exclusionReason={stock.scoring_exclusion_reason}
                          />
                        </td>
                        {/* Score Column - Locked in preview mode */}
                        <td className="p-2 sm:p-3 text-center">
                          {isPreviewMode ? (
                            <div 
                              className="flex items-center justify-center gap-1 text-gray-500"
                              title="Connectez-vous pour voir le score"
                              data-testid="score-locked"
                            >
                              <Lock className="w-4 h-4" />
                            </div>
                          ) : stock.valoramap_score_basis === 'no_data' ? (
                            // METHODOLOGY §4.6.3 / §4.7.1 — VN tickers, no calculable score
                            <ValoramapScoreBasisBadge basis="no_data" />
                          ) : stock.valoramap_score !== null && stock.valoramap_score !== undefined ? (
                            <TooltipProvider delayDuration={0}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="cursor-help inline-flex items-center gap-1.5">
                                    <ValoramapScoreBadge score={stock.valoramap_score} size="sm" />
                                    {/* METHODOLOGY §4.6.3 — visual indicator for reduced_financial basis */}
                                    <ValoramapScoreBasisBadge basis={stock.valoramap_score_basis} />
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="bg-[#1a1a2e] border-white/20 text-gray-200">
                                  <p className="text-xs">VALORAMAP Score : {stock.valoramap_score}/100 — {getScoreLabel(stock.valoramap_score)}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <span className="text-gray-600 text-sm">-</span>
                          )}
                        </td>
                        {/* Liquidity Score Column */}
                        <td className="p-2 sm:p-3 text-center">
                          {stock.liquidity_score !== null && stock.liquidity_score !== undefined ? (
                            <TooltipProvider delayDuration={0}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="cursor-help">
                                    <LiquidityBadgeCompact 
                                      score={stock.liquidity_score} 
                                      riskLevel={stock.liquidity_risk || 'MODERATE_RISK'} 
                                    />
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="bg-[#1a1a2e] border-white/20 text-gray-200">
                                  <p className="text-xs">Liquidité : {stock.liquidity_score}/100 — {getLiquidityLabel(stock.liquidity_score)}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <span className="text-gray-600 text-sm">-</span>
                          )}
                        </td>
                        {/* Quality (Red Flags) Column - Hidden if >80% N/A */}
                        {showQualityColumn && (
                          <td className="p-2 sm:p-3 text-center">
                            <RedFlagBadgeCompact 
                              qualityData={qualityCache[`${stock.market}-${stock.symbol}`]}
                              showLabel={false}
                            />
                          </td>
                        )}
                        {/* ADR Column */}
                        <td className="p-2 sm:p-3 text-center">
                          {stock.has_adr ? (
                            <TooltipProvider delayDuration={0}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="text-xs text-blue-400 cursor-help">{stock.adr_ticker || '✓'}</span>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="bg-[#1a1a2e] border-white/20 text-gray-200">
                                  <p className="text-xs">ADR disponible — cliquez sur l'action pour voir les détails d'accès</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <TooltipProvider delayDuration={0}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="text-gray-600 text-sm cursor-help">-</span>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="bg-[#1a1a2e] border-white/20 text-gray-200">
                                  <p className="text-xs">Pas d'ADR disponible</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </td>
                        {/* Earnings Column */}
                        <td className="p-2 sm:p-3 text-center">
                          {stock.next_earnings ? (
                            <TooltipProvider delayDuration={0}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className={`text-xs cursor-help ${
                                    stock.days_to_earnings !== null && stock.days_to_earnings <= 7 
                                      ? 'text-amber-400 font-medium' 
                                      : 'text-gray-400'
                                  }`}>
                                    {new Date(stock.next_earnings).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="bg-[#1a1a2e] border-white/20 text-gray-200">
                                  <p className="text-xs">
                                    Prochains résultats : {new Date(stock.next_earnings).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                                    {stock.days_to_earnings !== null && stock.days_to_earnings <= 7 && (
                                      <span className="text-amber-400 block mt-1">⚠️ Dans moins de 7 jours</span>
                                    )}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ) : (
                            <span className="text-gray-600 text-sm">-</span>
                          )}
                        </td>
                        {/* WATCHLIST CELL HIDDEN FOR LAUNCH
                        <td className="p-2 sm:p-3 text-center">
                          <button
                            onClick={() => toggleWatchlist(stock.symbol)}
                            className="p-1 rounded-lg hover:bg-white/10 transition-colors"
                          >
                            <Star className={`w-4 h-4 ${
                              watchlist.includes(stock.symbol)
                                ? 'fill-amber-400 text-amber-400'
                                : 'text-gray-500 hover:text-amber-400'
                            }`} />
                          </button>
                        </td>
                        */}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="p-4 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-sm text-gray-400">
                    Page {currentPage} sur {totalPages}
                  </div>
                  <div className="flex items-center gap-1 sm:gap-2 flex-wrap justify-center">
                    {/* Previous button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="border-white/10 text-gray-300 hover:bg-white/5 disabled:opacity-50"
                      data-testid="pagination-prev"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    
                    {/* Dynamic page buttons */}
                    {(() => {
                      const pages = [];
                      const showEllipsisStart = currentPage > 4;
                      const showEllipsisEnd = currentPage < totalPages - 3;
                      
                      // Always show page 1
                      pages.push(1);
                      
                      // Show start ellipsis
                      if (showEllipsisStart) {
                        pages.push('start-ellipsis');
                      }
                      
                      // Calculate range around current page
                      let rangeStart = Math.max(2, currentPage - 2);
                      let rangeEnd = Math.min(totalPages - 1, currentPage + 2);
                      
                      // Adjust range if near start or end
                      if (currentPage <= 4) {
                        rangeStart = 2;
                        rangeEnd = Math.min(6, totalPages - 1);
                      } else if (currentPage >= totalPages - 3) {
                        rangeStart = Math.max(2, totalPages - 5);
                        rangeEnd = totalPages - 1;
                      }
                      
                      // Add range pages
                      for (let i = rangeStart; i <= rangeEnd; i++) {
                        if (i > 1 && i < totalPages) {
                          pages.push(i);
                        }
                      }
                      
                      // Show end ellipsis
                      if (showEllipsisEnd) {
                        pages.push('end-ellipsis');
                      }
                      
                      // Always show last page if > 1
                      if (totalPages > 1) {
                        pages.push(totalPages);
                      }
                      
                      // Remove duplicates and sort
                      const uniquePages = [...new Set(pages)];
                      
                      return uniquePages.map((page, idx) => {
                        if (page === 'start-ellipsis' || page === 'end-ellipsis') {
                          return (
                            <span key={page} className="text-gray-500 px-1">...</span>
                          );
                        }
                        return (
                          <Button
                            key={`page-${page}`}
                            variant={currentPage === page ? "default" : "outline"}
                            size="sm"
                            onClick={() => setCurrentPage(page)}
                            className={currentPage === page 
                              ? "bg-emerald-500 text-white min-w-[36px]" 
                              : "border-white/10 text-gray-300 hover:bg-white/5 min-w-[36px]"
                            }
                            data-testid={`pagination-page-${page}`}
                          >
                            {page}
                          </Button>
                        );
                      });
                    })()}
                    
                    {/* Next button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="border-white/10 text-gray-300 hover:bg-white/5 disabled:opacity-50"
                      data-testid="pagination-next"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {/* Go to page input */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-400 whitespace-nowrap">Aller à :</span>
                    <input
                      type="number"
                      min="1"
                      max={totalPages}
                      placeholder={currentPage.toString()}
                      className="w-16 px-2 py-1 text-sm bg-white/5 border border-white/10 rounded text-white placeholder-gray-500 focus:outline-none focus:border-emerald-500"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const value = parseInt(e.target.value);
                          if (value >= 1 && value <= totalPages) {
                            setCurrentPage(value);
                            e.target.value = '';
                          }
                        }
                      }}
                      data-testid="pagination-goto-input"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        const input = e.target.closest('div').querySelector('input');
                        const value = parseInt(input.value);
                        if (value >= 1 && value <= totalPages) {
                          setCurrentPage(value);
                          input.value = '';
                        }
                      }}
                      className="border-white/10 text-gray-300 hover:bg-white/5 px-2"
                      data-testid="pagination-goto-btn"
                    >
                      OK
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ScreenerPage;
